import { useId } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { useDsT } from '../../services/i18n';
import type { MapProvider } from './MapProviderContext';
import { routeStops, seriesBg, seriesFill, seriesStroke, type MapLatLng, type MapMarkerKind, type MapShape, type MapShapeTone, type MapViewProps } from './MapView';

/**
 * L-13c (G-002, D-333): a map provider with no base map. It places every point that carries coordinates where it really
 * is — Web Mercator, fitted to the points — over a latitude/longitude grid, and draws the route through them. No tiles are
 * fetched, so it needs no vendor, no key and no network; a tiled provider (MapLibre with a tile service) replaces it
 * through the same {@link MapProvider} context once a tile vendor is chosen (HUMAN_DECISIONS).
 */

const W = 640;
const PAD = 40;

interface Plotted {
  id: string;
  label: string;
  kind: MapMarkerKind;
  lat: number;
  lng: number;
}

/** 0–1 Web Mercator coordinates of a point. */
function project(lat: number, lng: number): [number, number] {
  const clamped = Math.max(-85, Math.min(85, lat));
  const rad = (clamped * Math.PI) / 180;
  return [(lng + 180) / 360, (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2];
}

/** A grid step (degrees) that gives a handful of lines across the span. */
function gridStep(spanDegrees: number): number {
  const steps = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 20, 30, 45];
  return steps.find((s) => spanDegrees / s <= 6) ?? 60;
}

const hasCoordinates = (p: { lat?: number; lng?: number }): p is { lat: number; lng: number } =>
  typeof p.lat === 'number' && typeof p.lng === 'number' && Number.isFinite(p.lat) && Number.isFinite(p.lng);

/** The points of the props that can be placed: route stops, the position, then the markers. */
export function plottablePoints(props: Pick<MapViewProps, 'route' | 'position' | 'markers'>): Plotted[] {
  const stops = routeStops(props.route);
  const out: Plotted[] = [];
  stops.forEach((stop, i) => {
    if (hasCoordinates(stop)) out.push({ id: `stop-${i}`, label: stop.name, kind: i === 0 ? 'origin' : i === stops.length - 1 ? 'destination' : 'via', lat: stop.lat, lng: stop.lng });
  });
  if (props.position && hasCoordinates(props.position)) out.push({ id: 'position', label: props.position.label, kind: 'position', lat: props.position.lat, lng: props.position.lng });
  for (const m of props.markers ?? []) {
    if (hasCoordinates(m)) out.push({ id: `marker-${m.id}`, label: m.label, kind: m.kind ?? 'poi', lat: m.lat, lng: m.lng });
  }
  return out;
}

const METRES_PER_DEGREE = 111_320;

/** The extent of a shape as coordinates to fit: a polygon's corners, a circle's four compass points. */
export function shapeExtent(shape: MapShape): MapLatLng[] {
  if (shape.kind === 'polygon') return shape.points.filter(hasCoordinates);
  if (!hasCoordinates(shape.center) || !(shape.radiusM > 0)) return hasCoordinates(shape.center) ? [shape.center] : [];
  const dLat = shape.radiusM / METRES_PER_DEGREE;
  const dLng = shape.radiusM / (METRES_PER_DEGREE * Math.max(0.01, Math.cos((shape.center.lat * Math.PI) / 180)));
  const { lat, lng } = shape.center;
  return [{ lat: lat + dLat, lng }, { lat: lat - dLat, lng }, { lat, lng: lng + dLng }, { lat, lng: lng - dLng }];
}

const SHAPE_TONE: Record<MapShapeTone, string> = {
  zone: 'fill-map-zone stroke-map-zone-border',
  accent: 'fill-accent/15 stroke-accent',
  warning: 'fill-warning/15 stroke-warning',
  danger: 'fill-danger/15 stroke-danger',
};

function SchematicMap(props: MapViewProps) {
  const { height = 240, title: titleProp, emptyLabel, routes = [], shapes = [] } = props;
  const t = useDsT();
  const id = useId();
  const title = titleProp ?? t('ds.mapView.title', 'Route map');
  const points = plottablePoints(props);
  const layers = routes.map((r, i) => ({ ...r, series: r.series ?? i + 1, points: r.points.filter((p): p is typeof p & MapLatLng => hasCoordinates(p)) })).filter((r) => r.points.length > 0);
  const areas = shapes.filter((sh) => shapeExtent(sh).length > 0);
  // Everything that must fit in the frame: the points, every route layer's points, every shape's extent.
  const extent: MapLatLng[] = [...points, ...layers.flatMap((r) => r.points), ...areas.flatMap(shapeExtent)];
  const H = Math.max(160, height);

  if (!extent.length) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-border bg-map-land type-body text-fg-muted" style={{ height: H }}>
        <Icon name="map" size={20} aria-hidden="true" />
        {emptyLabel ?? t('ds.mapView.empty', 'No route to show')}
      </div>
    );
  }

  // Fit everything into the frame, one scale for both axes so distances keep their shape.
  const fitted = extent.map((p) => project(p.lat, p.lng));
  const projected = points.map((p) => project(p.lat, p.lng));
  const xs = fitted.map((p) => p[0]);
  const ys = fitted.map((p) => p[1]);
  const minSpan = 0.0005;
  let [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  if (x1 - x0 < minSpan) [x0, x1] = [x0 - minSpan / 2, x1 + minSpan / 2];
  if (y1 - y0 < minSpan) [y0, y1] = [y0 - minSpan / 2, y1 + minSpan / 2];
  const scale = Math.min((W - PAD * 2) / (x1 - x0), (H - PAD * 2) / (y1 - y0));
  const ox = (W - (x1 - x0) * scale) / 2;
  const oy = (H - (y1 - y0) * scale) / 2;
  const toScreen = ([x, y]: [number, number]): [number, number] => [ox + (x - x0) * scale, oy + (y - y0) * scale];
  const screen = projected.map(toScreen);

  // The grid: meridians and parallels at a round step across what is visible.
  const lngs = extent.map((p) => p.lng);
  const lats = extent.map((p) => p.lat);
  const step = gridStep(Math.max(Math.max(...lngs) - Math.min(...lngs), Math.max(...lats) - Math.min(...lats), 0.05) * 1.6);
  const west = (0 - ox) / scale + x0;
  const east = (W - ox) / scale + x0;
  const lngFrom = Math.floor((west * 360 - 180) / step) * step;
  const lngTo = east * 360 - 180;
  const meridians: number[] = [];
  for (let g = lngFrom; g <= lngTo && meridians.length < 40; g += step) meridians.push(g);
  const unproject = (y: number) => (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
  const north = unproject((0 - oy) / scale + y0);
  const south = unproject((H - oy) / scale + y0);
  const parallels: number[] = [];
  for (let g = Math.floor(south / step) * step; g <= north && parallels.length < 40; g += step) parallels.push(g);

  const route = points.map((p, i) => ({ p, s: screen[i]! })).filter(({ p }) => p.kind === 'origin' || p.kind === 'via' || p.kind === 'destination');
  const summary = `${title}: ${[...layers.map((r) => r.label), ...areas.map((a) => a.label ?? a.id), ...points.map((p) => p.label)].join(', ')}`;
  const at = (p: MapLatLng) => toScreen(project(p.lat, p.lng));
  const round = (n: number) => Number(n.toFixed(Math.max(0, -Math.floor(Math.log10(step)))));

  return (
    <figure aria-labelledby={`${id}-title`} className="m-0 flex flex-col overflow-hidden rounded-lg border border-border bg-map-water text-fg" data-schematic-map>
      <figcaption id={`${id}-title`} className="sr-only">
        {summary}
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="xMidYMid meet" role="img" aria-label={summary} className="block">
        <rect x={0} y={0} width={W} height={H} className="fill-map-land" />
        {meridians.map((g) => {
          const [x] = toScreen(project(0, g));
          return (
            <g key={`m${g}`}>
              <line x1={x} y1={0} x2={x} y2={H} strokeWidth={1} className="stroke-map-boundary" strokeDasharray="2 4" />
              {x > 8 && x < W - 36 ? <text x={x + 3} y={H - 4} fontSize={9} className="fill-map-label tnum">{`${round(Math.abs(g))}°${g < 0 ? 'W' : 'E'}`}</text> : null}
            </g>
          );
        })}
        {parallels.map((g) => {
          const [, y] = toScreen(project(g, 0));
          return (
            <g key={`p${g}`}>
              <line x1={0} y1={y} x2={W} y2={y} strokeWidth={1} className="stroke-map-boundary" strokeDasharray="2 4" />
              {y > 14 && y < H - 14 ? <text x={3} y={y - 3} fontSize={9} className="fill-map-label tnum">{`${round(Math.abs(g))}°${g < 0 ? 'S' : 'N'}`}</text> : null}
            </g>
          );
        })}
        {areas.map((sh) => {
          const tone = SHAPE_TONE[sh.tone ?? 'zone'];
          if (sh.kind === 'polygon') {
            return (
              <g key={`shape-${sh.id}`} data-shape-kind="polygon" data-shape={sh.id}>
                <polygon points={sh.points.filter(hasCoordinates).map((p) => at(p).join(',')).join(' ')} strokeWidth={2} strokeLinejoin="round" className={tone} />
              </g>
            );
          }
          const [cx, cy] = at(sh.center);
          // The Mercator scale at the centre's latitude: the northern compass point's distance on screen is the radius.
          const r = sh.radiusM > 0 ? Math.abs(cy - at(shapeExtent(sh)[0]!)[1]) : 0;
          return (
            <g key={`shape-${sh.id}`} data-shape-kind="circle" data-shape={sh.id}>
              <circle cx={cx} cy={cy} r={Math.max(3, r)} strokeWidth={2} className={tone} />
              <circle cx={cx} cy={cy} r={2.5} className="fill-map-zone-border" />
            </g>
          );
        })}
        {layers.map((layer) => (
          <g key={`route-${layer.id}`} data-route-layer={layer.id}>
            {layer.points.length > 1 ? (
              <polyline points={layer.points.map((p) => at(p).join(',')).join(' ')} fill="none" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className={seriesStroke(layer.series)} />
            ) : null}
            {layer.points.map((p, i) => {
              const [x, y] = at(p);
              return (
                <g key={i} transform={`translate(${x} ${y})`}>
                  <circle r={i === 0 ? 6 : 4} strokeWidth={2} className={cn(seriesFill(layer.series), 'stroke-map-land')} />
                  {p.label ? (
                    <text x={0} y={-9} textAnchor={x > W - 80 ? 'end' : x < 80 ? 'start' : 'middle'} fontSize={11} className="fill-fg">
                      {p.label}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        ))}
        {route.length > 1 ? <polyline points={route.map(({ s }) => s.join(',')).join(' ')} fill="none" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="stroke-map-route" /> : null}
        {points.map((p, i) => {
          const [x, y] = screen[i]!;
          const big = p.kind === 'origin' || p.kind === 'destination' || p.kind === 'position';
          return (
            <g key={p.id} data-point-kind={p.kind} transform={`translate(${x} ${y})`}>
              {p.kind === 'position' ? <circle r={12} className="fill-accent/20" /> : null}
              <circle r={big ? 7 : 5} strokeWidth={2} className={cn(p.kind === 'position' ? 'fill-accent stroke-map-land' : p.kind === 'alert' ? 'fill-danger stroke-map-land' : big ? 'fill-map-node stroke-map-land' : 'fill-map-land stroke-map-route')} />
              <text x={0} y={-12} textAnchor={x > W - 80 ? 'end' : x < 80 ? 'start' : 'middle'} fontSize={12} fontWeight={600} className="fill-fg">
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>
      {layers.length > 1 || areas.some((a) => a.label) ? (
        <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 border-t border-border bg-surface-1 px-3 py-1.5 type-caption text-fg-muted" aria-label={title} data-schematic-legend>
          {layers.map((layer) => (
            <li key={layer.id} className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className={cn('inline-block h-1 w-4 rounded-full', seriesBg(layer.series))} />
              {layer.label}
            </li>
          ))}
          {areas.filter((a) => a.label).map((a) => (
            <li key={a.id} className="inline-flex items-center gap-1.5">
              <Icon name={a.kind === 'circle' ? 'circle-dot' : 'square'} size={12} aria-hidden="true" className="text-fg-subtle" />
              {a.label}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-center gap-2 border-t border-border bg-surface-1 px-3 py-1.5 type-caption text-fg-muted" data-schematic-note>
        <Icon name="map" size={12} aria-hidden="true" />
        {t('ds.mapView.schematic', 'Schematic map — points at their coordinates, no base map')}
      </div>
    </figure>
  );
}

/** The schematic provider: install it with `<MapProviderProvider provider={schematicMapProvider}>`. */
export const schematicMapProvider: MapProvider = {
  name: 'schematic',
  render: (props) => <SchematicMap {...props} />,
};
